/**
 * Roles and Permissions Configuration
 *
 * Defines the role-based access control (RBAC) system for the application.
 * Includes role definitions, permission sets for organizations and fields.
 */

// ============================================
// Type Definitions
// ============================================

/**
 * Available role types in the system
 */
export type RoleType = 'owner' | 'admin' | 'manager' | 'editor' | 'member' | 'viewer';

/**
 * Role name mapping (role key to display name)
 */
export type RoleNames = Record<RoleType, string>;

/**
 * Role level mapping (role key to numeric level)
 * Higher number = more permissions
 */
export type RoleLevel = Record<RoleType, number>;

/**
 * Generic permission types
 */
export type GenericPermission =
    | 'view'
    | 'edit'
    | 'delete'
    | 'create'
    | 'manage'
    | 'approve'
    | 'export'
    | 'import';

/**
 * Organization-specific permissions
 */
export type OrganizationPermission =
    | 'org:view'
    | 'org:view_basic_info'
    | 'org:edit_basic_info'
    | 'org:edit_detailed_info'
    | 'org:delete_org'
    | 'org:transfer_ownership'
    | 'org:manage_members'
    | 'org:invite_member'
    | 'org:remove_member'
    | 'org:change_member_role'
    | 'org:manage_fields'
    | 'org:create_field'
    | 'org:delete_field'
    | 'org:create_announcement'
    | 'org:delete_announcement';

/**
 * Field-specific permissions
 */
export type FieldPermission =
    | 'field:view'
    | 'field:view_basic_info'
    | 'field:edit_basic_info'
    | 'field:delete_field'
    | 'field:manage_members'
    | 'field:invite_member'
    | 'field:remove_member'
    | 'field:change_member_role'
    | 'field:transfer_ownership'
    | 'field:edit_role'
    | 'field:remove_role'
    | 'field:create_report';

/**
 * All permission types combined
 */
export type Permission = GenericPermission | OrganizationPermission | FieldPermission;

/**
 * Permission descriptions mapping
 */
export type PermissionDescriptions = Record<Permission, string>;

/**
 * Permission set for a specific role
 */
export type RolePermissions = Record<RoleType, Permission[]>;

/**
 * Permission sets for organization and field contexts
 */
export interface PermissionSets {
    /** Permissions for organization context */
    organization: RolePermissions;
    /** Permissions for field context */
    field: RolePermissions;
}

/**
 * All available permissions grouped by context
 */
export interface AllPermissions {
    /** All organization permissions */
    organization: Permission[];
    /** All field permissions */
    field: Permission[];
}

// ============================================
// Role Name Mapping (Chinese)
// ============================================

/**
 * Role name display mapping
 * Maps role keys to Chinese display names
 */
export const roleNames: RoleNames = {
    'owner': '擁有者',
    'admin': '管理員',
    'manager': '主管',
    'editor': '編輯者',
    'member': '成員',
    'viewer': '檢視者'
};

// ============================================
// Role Level Definition
// ============================================

/**
 * Role hierarchy levels
 * Higher number indicates higher privilege level
 *
 * - owner (5): Full control
 * - admin (4): Administrative access
 * - manager (3): Supervisory access
 * - editor (2): Content editing access
 * - member (1): Basic membership
 * - viewer (0): Read-only access
 */
export const roleLevel: RoleLevel = {
    'owner': 5,
    'admin': 4,
    'manager': 3,
    'editor': 2,
    'member': 1,
    'viewer': 0
};

// ============================================
// Permission Descriptions (Chinese)
// ============================================

/**
 * Permission description mapping
 * Maps permission keys to Chinese descriptions
 */
export const permissionDescriptions: PermissionDescriptions = {
    // Generic permissions
    'view': '查看',
    'edit': '編輯',
    'delete': '刪除',
    'create': '創建',
    'manage': '管理',
    'approve': '審批',
    'export': '匯出',
    'import': '匯入',

    // Organization permissions
    'org:view': '查看組織',
    'org:view_basic_info': '查看組織基本資訊',
    'org:edit_basic_info': '編輯組織基本資訊',
    'org:edit_detailed_info': '編輯組織詳細資訊',
    'org:delete_org': '刪除組織',
    'org:transfer_ownership': '轉移組織所有權',

    // Organization member management
    'org:manage_members': '管理組織成員',
    'org:invite_member': '邀請成員',
    'org:remove_member': '移除成員',
    'org:change_member_role': '變更成員角色',

    // Organization field management
    'org:manage_fields': '管理案場',
    'org:create_field': '創建案場',
    'org:delete_field': '刪除案場',

    // Organization announcement management
    'org:create_announcement': '創建公告',
    'org:delete_announcement': '刪除公告',

    // Field permissions
    'field:view': '查看案場',
    'field:view_basic_info': '查看案場基本資訊',
    'field:edit_basic_info': '編輯案場基本資訊',
    'field:delete_field': '刪除案場',

    // Field member management
    'field:manage_members': '管理案場成員',
    'field:invite_member': '邀請案場成員',
    'field:remove_member': '移除案場成員',
    'field:change_member_role': '變更案場成員角色',

    'field:transfer_ownership': '轉移案場所有權',
    'field:edit_role': '修改案場角色權限設定',
    'field:remove_role': '刪除案場角色設定',

    // Field report management
    'field:create_report': '創建報告'
};

// ============================================
// Default Permission Sets
// ============================================

/**
 * Default permission sets for each role in organization and field contexts
 */
export const permissionSets: PermissionSets = {
    organization: {
        'owner': [
            'org:view',
            'org:view_basic_info',
            'org:edit_basic_info',
            'org:edit_detailed_info',
            'org:delete_org',
            'org:transfer_ownership',
            'org:create_field',
            'org:delete_field',
            'org:manage_members',
            'org:invite_member',
            'org:remove_member',
            'org:change_member_role',
            'org:manage_fields',
            'org:create_announcement',
            'org:delete_announcement',
            // Field permissions
            'field:view',
            'field:view_basic_info',
            'field:edit_basic_info',
            'field:delete_field',
            'field:transfer_ownership',
            'field:manage_members',
            'field:invite_member',
            'field:remove_member',
            'field:change_member_role',
            'field:edit_role',
            'field:remove_role',
            'field:create_report'
        ],
        'admin': [
            'org:view',
            'org:view_basic_info',
            'org:edit_basic_info',
            'org:edit_detailed_info',
            'org:manage_members',
            'org:invite_member',
            'org:remove_member',
            'org:change_member_role',
            'org:manage_fields',
            'org:create_field',
            'org:delete_field',
            'org:create_announcement',
            // Field permissions
            'field:view',
            'field:view_basic_info',
            'field:edit_basic_info',
            'field:manage_members',
            'field:invite_member',
            'field:remove_member',
            'field:change_member_role',
            'field:edit_role',
            'field:create_report'
        ],
        'manager': [
            'org:view',
            'org:view_basic_info',
            'org:edit_basic_info',
            'org:manage_members',
            'org:invite_member',
            'org:manage_fields',
            'org:create_field',
            'org:create_announcement',
            // Field permissions
            'field:view',
            'field:view_basic_info',
            'field:edit_basic_info',
            'field:manage_members',
            'field:invite_member',
            'field:create_report'
        ],
        'editor': [
            'org:view',
            'org:view_basic_info',
            'org:edit_basic_info',
            'org:manage_fields',
            // Field permissions
            'field:view',
            'field:view_basic_info',
            'field:edit_basic_info',
            'field:create_report'
        ],
        'member': [
            'org:view',
            'org:view_basic_info',
            'org:manage_fields',
            // Field permissions
            'field:view',
            'field:view_basic_info'
        ],
        'viewer': [
            'org:view',
            'org:view_basic_info',
            'org:manage_fields',
            // Field permissions
            'field:view',
            'field:view_basic_info'
        ]
    },
    field: {
        'owner': [
            'field:view',
            'field:view_basic_info',
            'field:edit_basic_info',
            'field:delete_field',
            'field:transfer_ownership',
            'field:manage_members',
            'field:invite_member',
            'field:remove_member',
            'field:change_member_role',
            'field:edit_role',
            'field:remove_role',
            'field:create_report'
        ],
        'admin': [
            'field:view',
            'field:view_basic_info',
            'field:edit_basic_info',
            'field:manage_members',
            'field:invite_member',
            'field:remove_member',
            'field:change_member_role',
            'field:edit_role',
            'field:create_report'
        ],
        'manager': [
            'field:view',
            'field:view_basic_info',
            'field:edit_basic_info',
            'field:manage_members',
            'field:invite_member',
            'field:create_report'
        ],
        'editor': [
            'field:view',
            'field:view_basic_info',
            'field:edit_basic_info',
            'field:create_report'
        ],
        'member': [
            'field:view',
            'field:view_basic_info'
        ],
        'viewer': [
            'field:view',
            'field:view_basic_info'
        ]
    }
};

// ============================================
// Derived Permission Lists
// ============================================

/**
 * Extract all unique permissions from permission sets
 * Useful for validation and UI rendering
 */
export const allPermissions: AllPermissions = {
    organization: [...new Set(
        Object.values(permissionSets.organization).flatMap(permissions => permissions)
    )] as Permission[],
    field: [...new Set(
        Object.values(permissionSets.field).flatMap(permissions => permissions)
    )] as Permission[]
};

// ============================================
// Helper Functions
// ============================================

/**
 * Get display name for a role
 * @param role - The role key
 * @returns The Chinese display name for the role
 */
export function getRoleName(role: RoleType): string {
    return roleNames[role] || role;
}

/**
 * Get numeric level for a role
 * @param role - The role key
 * @returns The numeric level (0-5)
 */
export function getRoleLevel(role: RoleType): number {
    return roleLevel[role] ?? 0;
}

/**
 * Check if a role has a specific permission in a context
 * @param role - The role to check
 * @param permission - The permission to check for
 * @param context - The context ('organization' or 'field')
 * @returns Whether the role has the permission
 */
export function hasPermission(
    role: RoleType,
    permission: Permission,
    context: 'organization' | 'field'
): boolean {
    const permissions = permissionSets[context][role];
    return permissions?.includes(permission) ?? false;
}

/**
 * Get all permissions for a role in a context
 * @param role - The role to get permissions for
 * @param context - The context ('organization' or 'field')
 * @returns Array of permissions
 */
export function getPermissions(
    role: RoleType,
    context: 'organization' | 'field'
): Permission[] {
    return permissionSets[context][role] || [];
}

/**
 * Compare two roles and return which has higher privilege
 * @param role1 - First role to compare
 * @param role2 - Second role to compare
 * @returns Positive if role1 > role2, negative if role1 < role2, 0 if equal
 */
export function compareRoles(role1: RoleType, role2: RoleType): number {
    return getRoleLevel(role1) - getRoleLevel(role2);
}

export default {
    roleNames,
    roleLevel,
    permissionDescriptions,
    permissionSets,
    allPermissions,
    getRoleName,
    getRoleLevel,
    hasPermission,
    getPermissions,
    compareRoles
};
